import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Home,
  Compass,
  User,
  PlusSquare,
  Search as SearchIcon,
  Bell,
  Sun,
  Moon,
  Settings as SettingsIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { HeaderBrand } from "@/components/Brand";
import { OpinionBubbleIcon } from "@/components/OpinionBubble";
import UserAvatar from "@/components/UserAvatar";
import api, { unwrap } from "@/lib/api";
import { EP } from "@/lib/endpoints";

/**
 * AppShell — responsive shell for the whole authenticated app.
 *
 * Two form factors, ONE source of truth for the routes / theme / auth:
 *
 *   • **Mobile / small tablet** (`< md`): 1:1 iOS port — top bar,
 *     560-px centered column, and the 78-px glass bottom tab bar
 *     with the rainbow-underlined active pill.  Nothing changes.
 *
 *   • **Desktop / iPad+** (`≥ md` — 768 px and up): X/Instagram-web
 *     layout — a persistent LEFT SIDEBAR with the brand mark + full
 *     nav (Home, Discover, Create, Opinions, Notifications, Profile,
 *     Settings), an auth avatar/handle at the bottom, and the
 *     content in a centered max-w-[600px] column with a hairline
 *     divider on either side.  The mobile top bar and bottom tab
 *     bar are hidden.
 *
 * Both surfaces use the same buttons, colours and interactions — this
 * is a layout switch, not a redesign.
 */

const BRONTO_RAINBOW =
  "linear-gradient(90deg, #FFD600, #FF7A00, #FF0069, #D300C5, #7638FA)";

// iOS uses hardcoded inactive-tint hex values, matching `theme.mutedText`.
const INACTIVE_LIGHT = "#737373";
const INACTIVE_DARK = "#A8A8A8";

/**
 * Single tab icon.  Wraps every icon in a 48×38 box, focused state grows
 * the icon by 1px and paints the rainbow underline.
 */
function TabIcon({ Icon, focused, inactiveColor, size = 24 }) {
  const iconSize = focused ? size + 1 : size;
  return (
    <span
      className="relative inline-flex items-center justify-center"
      style={{ width: 48, height: 38 }}
    >
      <Icon
        size={iconSize}
        strokeWidth={1.8}
        style={{ color: focused ? "var(--fg-tab, hsl(var(--foreground)))" : inactiveColor }}
      />
      {focused && (
        <span
          className="absolute"
          style={{
            bottom: 0,
            width: 20,
            height: 3,
            borderRadius: 1.5,
            background: BRONTO_RAINBOW,
          }}
        />
      )}
    </span>
  );
}

function BottomTabBar({ onCreate }) {
  const location = useLocation();
  const { theme } = useTheme();
  const inactiveColor = theme === "dark" ? INACTIVE_DARK : INACTIVE_LIGHT;

  const isActive = (to) => {
    if (to === "/home") return location.pathname === "/home";
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  return (
    <nav
      className="fixed bottom-0 left-1/2 -translate-x-1/2 z-40 w-full max-w-[560px] border-t border-border backdrop-blur-2xl bg-background/85"
      style={{
        paddingBottom: "env(safe-area-inset-bottom)",
        // iOS: height 78, paddingTop 6, hairline top border, elevation 0.
        // Content underneath scrolls (FeedList adds a ~100px footer spacer).
      }}
      data-testid="app-bottom-tabs"
    >
      <div className="h-[78px] pt-1.5 flex items-center justify-around">
        {/* Home */}
        <NavLink
          to="/home"
          end
          aria-label="Home"
          className="flex-1 h-full flex items-center justify-center active:opacity-70 transition-opacity"
          data-testid="tab-home"
        >
          <TabIcon Icon={Home} focused={isActive("/home")} inactiveColor={inactiveColor} />
        </NavLink>

        {/* Discover */}
        <NavLink
          to="/discover"
          aria-label="Discover"
          className="flex-1 h-full flex items-center justify-center active:opacity-70 transition-opacity"
          data-testid="tab-discover"
        >
          <TabIcon Icon={Compass} focused={isActive("/discover")} inactiveColor={inactiveColor} />
        </NavLink>

        {/* Create — never navigates; opens compose modal like iOS tabPress. */}
        <button
          type="button"
          onClick={onCreate}
          aria-label="Create"
          className="flex-1 h-full flex items-center justify-center active:opacity-70 transition-opacity"
          data-testid="tab-create"
        >
          <PlusSquare size={26} strokeWidth={1.8} style={{ color: inactiveColor }} />
        </button>

        {/* Opinions */}
        <NavLink
          to="/opinions"
          aria-label="Opinions"
          className="flex-1 h-full flex items-center justify-center active:opacity-70 transition-opacity"
          data-testid="tab-opinions"
        >
          <TabIcon
            Icon={OpinionBubbleIcon}
            focused={isActive("/opinions")}
            inactiveColor={inactiveColor}
          />
        </NavLink>

        {/* Profile */}
        <NavLink
          to="/profile"
          aria-label="Profile"
          className="flex-1 h-full flex items-center justify-center active:opacity-70 transition-opacity"
          data-testid="tab-profile"
        >
          <TabIcon Icon={User} focused={isActive("/profile")} inactiveColor={inactiveColor} />
        </NavLink>
      </div>
    </nav>
  );
}

/**
 * TopBar — 46px, HeaderBrand height 27 on the left; Search · Bell · Sun/Moon
 * on the right, each 23px, strokeWidth 1.7, no unread badges (per spec).
 */
function TopBar() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(EP.notifications(1, 20));
        const d = unwrap(res.data);
        const items =
          d?.items || d?.notifications || d?.data?.items || (Array.isArray(d) ? d : []);
        if (!cancelled) setUnread(items.filter((n) => !(n.read || n.is_read)).length);
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-2xl bg-background/85"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="h-[46px] px-4 flex items-center justify-between max-w-[560px] mx-auto">
        <button
          type="button"
          onClick={() => navigate("/home")}
          className="flex items-center active:opacity-70 transition-opacity"
          aria-label="Home"
          data-testid="topbar-brand"
        >
          <HeaderBrand height={27} />
        </button>
        <div className="flex items-center" style={{ gap: 22 }}>
          <button
            type="button"
            onClick={() => navigate("/search")}
            className="text-foreground hover:opacity-70 active:opacity-50 transition-opacity"
            aria-label="Search"
            data-testid="home-search"
          >
            <SearchIcon size={23} strokeWidth={1.7} />
          </button>
          <button
            type="button"
            onClick={() => navigate("/notifications")}
            className="relative text-foreground hover:opacity-70 active:opacity-50 transition-opacity"
            aria-label="Notifications"
            data-testid="home-notifications"
          >
            <Bell size={23} strokeWidth={1.7} />
            {unread > 0 && (
              <span
                className="absolute -top-0.5 -right-1 min-w-[14px] h-[14px] px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center leading-none"
                style={{ background: "#FF3B30" }}
                data-testid="topbar-notifications-badge"
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={toggle}
            className="text-foreground hover:opacity-70 active:opacity-50 transition-opacity"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="home-theme-toggle"
          >
            {theme === "dark" ? (
              <Sun size={23} strokeWidth={1.7} />
            ) : (
              <Moon size={23} strokeWidth={1.7} />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

export default function AppShell({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // When AppShell is used as a Route element (the normal case), the
  // page renders through <Outlet />. When it's used as a WRAPPER
  // component around a specific page (e.g. PublicProfileGate for
  // authed guests hitting /:username), we render `children` instead
  // so the wrapper doesn't need a nested <Routes>.
  const body = children != null ? children : <Outlet context={{ user }} />;
  // Media-query hook — matches Tailwind's `md` breakpoint (768px).
  // Rendering only ONE shell (mobile OR desktop) avoids mounting
  // <Outlet /> twice, which would double-fire every page's data
  // fetches on load.  We hydrate as `null` on first paint to avoid a
  // flash of the wrong shell before matchMedia is available.
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.matchMedia("(min-width: 768px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setIsDesktop(e.matches);
    // `.addEventListener` is the modern API; guard for old Safari.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Top bar is only shown on the tab-root screens (home/discover/opinions/
  // profile) — mirrors iOS where each tab has its own header and stack
  // screens (post detail, settings, etc.) hide it.
  const tabRoots = ["/home", "/discover", "/opinions", "/profile"];
  const isTabRoot = tabRoots.some(
    (r) => location.pathname === r || location.pathname.startsWith(r + "/")
  );

  const openCompose = () => navigate("/create");

  // Render nothing on the first client-side tick until matchMedia
  // resolves — prevents a flash of the mobile shell on desktop.
  if (isDesktop === null) return <div className="min-h-[100dvh] bg-background" />;

  if (isDesktop) {
    return (
      <div className="min-h-[100dvh] bg-background text-foreground">
        <DesktopSidebar
          isActive={(to) =>
            to === "/home"
              ? location.pathname === "/home"
              : location.pathname === to ||
                location.pathname.startsWith(to + "/")
          }
          onCreate={openCompose}
          user={user}
        />
        {/* Content area sits directly beside the 240-px fixed sidebar
            and fills EVERYTHING to its right (no centered narrow strip).
            The row is: Feed (flexible, up to 640px for readability) +
            Right rail (320px on `lg`, hidden below).  A hairline
            divider separates the sidebar from the feed and the feed
            from the right rail — same pattern as X/Twitter's web app. */}
        <div className="pl-[240px] flex min-h-[100dvh]">
          <main
            className="flex-1 min-w-0 border-r border-border"
            data-testid="app-main-desktop"
          >
            {/* Internal pages set their own max-widths; the outer main
                is left-flush against the sidebar so the visible edge
                of the content butts up against the rail. */}
            {body}
          </main>
          <aside
            className="hidden lg:block w-[340px] shrink-0"
            data-testid="desktop-right-rail"
          >
            <DesktopRightRail />
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background text-foreground flex flex-col">
      <div className="w-full max-w-[560px] mx-auto flex-1 flex flex-col relative">
        {isTabRoot && <TopBar />}
        <main
          className="flex-1"
          style={{ paddingBottom: "calc(78px + env(safe-area-inset-bottom))" }}
          data-testid="app-main"
        >
          {body}
        </main>
      </div>
      <BottomTabBar onCreate={openCompose} />
    </div>
  );
}

// -----------------------------------------------------------------
// Desktop sidebar — X/Instagram-web style vertical rail with the
// brand mark on top, full nav in the middle, and the auth avatar +
// handle at the bottom.  All routes stay identical to the mobile
// tab bar's targets so switching form factors is a no-op mentally.
// -----------------------------------------------------------------
function DesktopSidebar({ isActive, onCreate, user }) {
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const items = [
    { to: "/home", label: "Home", Icon: Home },
    { to: "/discover", label: "Discover", Icon: Compass },
    { to: "/opinions", label: "Opinions", Icon: OpinionBubbleIcon },
    { to: "/search", label: "Search", Icon: SearchIcon },
    { to: "/notifications", label: "Notifications", Icon: Bell },
    { to: "/profile", label: "Profile", Icon: User },
    { to: "/settings", label: "Settings", Icon: SettingsIcon },
  ];

  return (
    <aside
      className="fixed left-0 top-0 h-[100dvh] w-[240px] border-r border-border bg-background/95 backdrop-blur-md flex flex-col z-40"
      data-testid="desktop-sidebar"
    >
      {/* Brand */}
      <button
        type="button"
        onClick={() => navigate("/home")}
        className="px-6 pt-6 pb-4 flex items-center gap-2 hover:opacity-80 transition-opacity"
        aria-label="BrontoY"
        data-testid="desktop-brand"
      >
        <HeaderBrand height={30} />
      </button>

      {/* Nav */}
      <nav className="flex-1 px-3 py-2 flex flex-col gap-1">
        {items.map(({ to, label, Icon }) => {
          const active = isActive(to);
          return (
            <NavLink
              key={to}
              to={to}
              className="flex items-center gap-4 rounded-full px-4 py-3 hover:bg-secondary transition-colors group"
              data-testid={`desktop-nav-${label.toLowerCase()}`}
            >
              <Icon
                size={24}
                strokeWidth={active ? 2.2 : 1.7}
                className={active ? "text-foreground" : "text-muted-foreground"}
              />
              <span
                className={`text-[16px] ${
                  active
                    ? "font-extrabold text-foreground"
                    : "font-medium text-muted-foreground group-hover:text-foreground"
                }`}
              >
                {label}
              </span>
            </NavLink>
          );
        })}

        {/* Create button — brand-purple full-width pill, iOS spec accent */}
        <button
          type="button"
          onClick={onCreate}
          className="mt-3 w-full h-11 rounded-full text-white font-extrabold text-[15px] flex items-center justify-center gap-2 active:opacity-85 transition-opacity"
          style={{ background: "hsl(var(--brand))" }}
          data-testid="desktop-create-btn"
        >
          <PlusSquare size={18} strokeWidth={2.2} />
          Create
        </button>
      </nav>

      {/* Theme toggle + user pill at the bottom */}
      <div className="px-3 pb-5 border-t border-border pt-3">
        <button
          type="button"
          onClick={toggle}
          className="w-full h-10 rounded-full flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors mb-2"
          aria-label="Toggle theme"
          data-testid="desktop-theme-toggle"
        >
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          <span className="text-[13px] font-medium">
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </span>
        </button>
        {user && (
          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="w-full flex items-center gap-3 rounded-full p-2 hover:bg-secondary transition-colors"
            data-testid="desktop-user-pill"
          >
            <UserAvatar user={user} size="md" />
            <div className="flex-1 min-w-0 text-left">
              <div className="text-foreground text-[14px] font-bold truncate">
                {user.display_name || user.username || "You"}
              </div>
              <div className="text-muted-foreground text-[12.5px] truncate">
                @{user.username || "you"}
              </div>
            </div>
          </button>
        )}
      </div>
    </aside>
  );
}

// -----------------------------------------------------------------
// Desktop right rail — X/Twitter-style column that fills the space
// to the right of the feed on `lg` and above.  Ships with a small
// search shortcut + a placeholder trending / suggested-people block
// so the rail feels productive out of the box.  Fully self-contained
// so it doesn't touch any of the feed pages' UI.
// -----------------------------------------------------------------
function DesktopRightRail() {
  const navigate = useNavigate();
  const [suggested, setSuggested] = useState([]);

  // Best-effort load of a few follow-suggestions using the same
  // endpoint the mobile RightPanel uses (EP.suggestedUsers — reliable
  // response shape).  Rows with a missing id are filtered out so a
  // partial payload silently hides the card instead of rendering
  // duplicate "undefined" keys.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(EP.suggestedUsers(1, 5));
        const d = unwrap(res.data);
        const list = Array.isArray(d)
          ? d
          : d?.users || d?.items || d?.suggested || d?.data || [];
        const valid = list
          .map((raw) => {
            const u = raw?.user && typeof raw.user === "object" ? raw.user : raw;
            return u && (u.user_id ?? u.id) != null ? u : null;
          })
          .filter(Boolean)
          .slice(0, 3);
        if (!cancelled) setSuggested(valid);
      } catch {
        /* silent — rail simply hides the suggestions block */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="sticky top-0 h-[100dvh] pt-4 pl-6 pr-4 overflow-y-auto"
      data-testid="right-rail-content"
    >
      {/* Search shortcut — mirrors iOS top-bar search button */}
      <button
        type="button"
        onClick={() => navigate("/search")}
        className="w-full h-11 rounded-full bg-secondary border border-border flex items-center gap-2.5 px-4 text-muted-foreground hover:bg-secondary/80 transition-colors"
        data-testid="right-rail-search"
      >
        <SearchIcon size={17} strokeWidth={1.9} />
        <span className="text-[14px]">Search BrontoY</span>
      </button>

      {/* Suggested to follow — Twitter's classic "Who to follow" card */}
      {suggested.length > 0 && (
        <div
          className="mt-4 rounded-2xl border border-border bg-card overflow-hidden"
          data-testid="right-rail-suggestions"
        >
          <div className="px-4 pt-3.5 pb-2 text-foreground text-[17px] font-extrabold">
            Suggested to follow
          </div>
          {suggested.map((u, i) => {
            const uid = u.user_id ?? u.id;
            const uname = u.username || "user";
            return (
              <button
                key={String(uid)}
                type="button"
                onClick={() => navigate(`/${uname}`)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/60 transition-colors ${
                  i === suggested.length - 1 ? "" : "border-b border-border"
                }`}
                data-testid={`right-rail-suggest-${uid}`}
              >
                <UserAvatar user={u} size="sm" />
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-foreground text-[14px] font-semibold truncate">
                    {u.display_name || uname}
                  </div>
                  <div className="text-muted-foreground text-[12.5px] truncate">
                    @{uname}
                  </div>
                </div>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => navigate("/discover")}
            className="w-full px-4 py-3 text-left text-[13.5px] font-semibold hover:bg-secondary/60 transition-colors"
            style={{ color: "hsl(var(--brand))" }}
            data-testid="right-rail-show-more"
          >
            Show more
          </button>
        </div>
      )}

      {/* Footer legalese — matches the pre-signup landing footer */}
      <nav className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground text-[12px]">
        <button
          type="button"
          onClick={() => navigate("/terms")}
          className="hover:underline underline-offset-4"
          data-testid="right-rail-terms"
        >
          Terms
        </button>
        <button
          type="button"
          onClick={() => navigate("/privacy")}
          className="hover:underline underline-offset-4"
          data-testid="right-rail-privacy"
        >
          Privacy
        </button>
        <button
          type="button"
          onClick={() => navigate("/guidelines")}
          className="hover:underline underline-offset-4"
          data-testid="right-rail-guidelines"
        >
          Community Guidelines
        </button>
        <span>© 2026 BrontoY</span>
      </nav>
    </div>
  );
}

